import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AddCoverImageIcon from '@wix/da-ds/pkg/Icons/24x24/AddCoverImage';
import EditIcon from '@wix/da-ds/pkg/Icons/24x24/Edit';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import { useMMDispatchActions } from '../../../_hocs/withOwnReduxStore';
import DeviationDumbView from '../../../../DeviationViews/Dumbo';
import { getFolderNameValidationErrorMessage, MAX_TITLE_LENGTH, } from '../../../../utils/isValidFolderName';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks';
import { useGallections } from '../../../_hocs/withGallections';
import { setGallectionName, setGallectionDescription, setGallectionPrice, } from '../../../actions';
import { useCurrentGallection } from '../../../_hocs/withCurrentGallection';
import { useCurrentSubfolder } from '../../../_hocs/withCurrentSubfolder';
import Textarea from '../../../../Textarea';
import { isGallectionManageable, canChangeGallectionDescription, areGallectionContentsManageable, } from '../../../helpers';
import FolderRibbon from '../../../../Primitives/FolderRibbon';
import DollarPriceInput from '../../../../DollarPriceInput';
import { normalizePremiumFolderData } from '../../../../utils/normalizePremiumFolderData';
import GallectionCoverSelector from '../../../_partials/GallectionCoverSelector';
import FolderCoverInfo from '../../../../FolderCoverInfo';
import { dollarsToPoints } from '../../../../utils/points';
import gallectionSidebarStyles from './GallectionSidebar.scss';
var MAX_DESCRIPTION_LENGTH = 1100;
export var GallectionSidebar = function () {
    var _a;
    var t = useTranslation().t;
    var gallections = useGallections().gallections;
    var currentGallectionFolder = useCurrentGallection().currentGallection;
    var currentSubfolder = useCurrentSubfolder().currentSubfolder;
    var isEarningsCalculator = useFlag('earnings_calculator');
    var currentGallection = currentSubfolder || currentGallectionFolder;
    var _b = useState(false), isShowingMediaSelector = _b[0], setIsShowingMediaSelector = _b[1];
    var showMediaSelector = useCallback(function () { return setIsShowingMediaSelector(true); }, [setIsShowingMediaSelector]);
    var hideMediaSelector = useCallback(function () { return setIsShowingMediaSelector(false); }, [setIsShowingMediaSelector]);
    var _c = useState(), nameErrorMessage = _c[0], setNameErrorMessage = _c[1];
    var _d = useState(), errorMessage = _d[0], setErrorMessage = _d[1];
    var _e = useState(), descriptionErrorMessage = _e[0], setDescriptionErrorMessage = _e[1];
    var _f = useState(''), price = _f[0], setPrice = _f[1];
    var actions = useMMDispatchActions({
        setGallectionName: setGallectionName,
        setGallectionDescription: setGallectionDescription,
        setGallectionPrice: setGallectionPrice,
    });
    var existingNames = useMemo(function () {
        return gallections
            .filter(function (gallection) { return gallection.folderId !== currentGallection.folderId; })
            .map(function (gallection) { return gallection.name; });
    }, [gallections, currentGallection.folderId]);
    var onChangeGallectionName = useCallback(function (name) {
        var message = getFolderNameValidationErrorMessage(name, t, existingNames, currentGallection.type === 'gallery');
        setNameErrorMessage(message);
        actions.setGallectionName({
            folderId: currentGallection.folderId,
            name: name,
        });
    }, [
        currentGallection.folderId,
        currentGallection.type,
        actions,
        existingNames,
        setNameErrorMessage,
        t,
    ]);
    var onChangeGallectionDescription = useCallback(function (description) {
        if (description.length >= MAX_DESCRIPTION_LENGTH) {
            setDescriptionErrorMessage(t('mediapopup.gallections.description.error.length'));
        }
        else {
            setDescriptionErrorMessage(null);
        }
        actions.setGallectionDescription({
            folderId: currentGallection.folderId,
            description: description,
        });
    }, [currentGallection.folderId, setDescriptionErrorMessage, actions, t]);
    var onChangeGallectionPrice = useCallback(function (value, msg) {
        if (!value || !!msg) {
            return;
        }
        actions.setGallectionPrice({
            folderId: currentGallection.folderId,
            pointsPrice: dollarsToPoints(value),
        });
    }, [currentGallection.folderId, actions]);
    var renderGallectionCoverBlock = function () {
        var gallectionThumb = currentGallection.thumb;
        var isThumbPremium = normalizePremiumFolderData(gallectionThumb === null || gallectionThumb === void 0 ? void 0 : gallectionThumb.premiumFolderData).isPremium;
        var canSetGallectionCover = areGallectionContentsManageable(currentGallection) &&
            (currentGallection === null || currentGallection === void 0 ? void 0 : currentGallection.type) !== 'stash';
        return (React.createElement("div", { className: gallectionSidebarStyles['cover-wrapper'] },
            gallectionThumb ? (React.createElement(DeviationDumbView, { useCoverMode: true, width: parseInt(gallectionSidebarStyles['width']), height: parseInt(gallectionSidebarStyles[isPaidFolder ? 'cover-height-paid-folder' : 'cover-height']), deviation: gallectionThumb, forceBlur: isThumbPremium })) : (React.createElement("div", { className: gallectionSidebarStyles['no-cover'] })),
            canSetGallectionCover && !gallectionThumb && (React.createElement("button", { className: gallectionSidebarStyles['cover-button-add'], onClick: showMediaSelector },
                React.createElement(AddCoverImageIcon, { className: gallectionSidebarStyles['cover-button-icon'] }),
                React.createElement("br", null),
                t('mediapopup.setGallectionCover'))),
            canSetGallectionCover && gallectionThumb && (React.createElement("div", { className: gallectionSidebarStyles['cover-buttons-wrapper'] },
                isThumbPremium && React.createElement(FolderCoverInfo, null),
                React.createElement("button", { className: gallectionSidebarStyles['cover-button-edit'], onClick: showMediaSelector },
                    React.createElement(EditIcon, { disableDefaultColors: true, size: IconSize.TINY }))))));
    };
    var renderSubscribersCount = function () {
        var _a, _b;
        return (React.createElement("div", { className: gallectionSidebarStyles['subscribers-counter'] },
            React.createElement("span", null, (_a = currentGallection.premiumFolderData) === null || _a === void 0 ? void 0 : _a.numSubscribers),
            ' ',
            t('common.supporters', {
                count: (_b = currentGallection.premiumFolderData) === null || _b === void 0 ? void 0 : _b.numSubscribers,
            })));
    };
    var renderGallectionNameInput = function () { return (React.createElement(TextInput, { id: "gallection-name", autoComplete: "off", validationResult: nameErrorMessage ? 'error' : undefined, details: nameErrorMessage, className: gallectionSidebarStyles['gallection-name'], label: t("mediapopup.gallections.".concat(currentGallection.type, ".name")), placeholder: t("mediapopup.gallections.".concat(currentGallection.type, ".name.placeholder")), value: currentGallection.name, onChange: function (ev) { return onChangeGallectionName(ev.target.value); }, readOnly: !isGallectionManageable(currentGallection), maxLength: MAX_TITLE_LENGTH })); };
    var renderDescriptionTextarea = function () {
        var descriptionKey;
        if (isPaidFolder) {
            descriptionKey = "mediapopup.gallections.".concat(currentGallection.type, ".paid.description.placeholder");
        }
        else if (isWatchToAccess) {
            descriptionKey = "mediapopup.gallections.".concat(currentGallection.type, ".watchers.description.placeholder");
        }
        else if (isPrivilegedFolder) {
            descriptionKey = "mediapopup.gallections.".concat(currentGallection.type, ".privileged.description.placeholder");
        }
        else {
            descriptionKey = "mediapopup.gallections.".concat(currentGallection.type, ".description.placeholder");
        }
        return (React.createElement(Textarea, { id: "gallection-description", wrapperClassName: gallectionSidebarStyles['gallection-description'], textareaClassName: gallectionSidebarStyles['gallection-description-textarea'], charactersRemainingClassName: gallectionSidebarStyles['gallection-description-chars-remaining'], label: t("mediapopup.gallections.".concat(currentGallection.type, ".description")), placeholder: t(descriptionKey), value: currentGallection.description, onChange: onChangeGallectionDescription, errorMessage: descriptionErrorMessage, maxLength: MAX_DESCRIPTION_LENGTH, autoSize: true }));
    };
    var renderPriceInput = function () {
        var _a;
        return (React.createElement(DollarPriceInput, { showFTUE: false, showEarnings: isEarningsCalculator, value: price !== ''
                ? price
                : ((_a = currentGallection.premiumFolderData) === null || _a === void 0 ? void 0 : _a.dollarPrice.toString()) || '', errorMessage: errorMessage, onChange: function (value, errorMsg) {
                setPrice(value);
                setErrorMessage(errorMsg);
                onChangeGallectionPrice(value, errorMsg);
            } }));
    };
    var _g = normalizePremiumFolderData(currentGallection.premiumFolderData), isPaidFolder = _g.isPaidFolder, isWatchToAccess = _g.isWatchToAccess, isPrivilegedFolder = _g.isPrivilegedFolder;
    return (React.createElement("div", { className: gallectionSidebarStyles['root'] },
        isShowingMediaSelector && (React.createElement(GallectionCoverSelector, { gallection: currentGallection, onClose: hideMediaSelector })),
        React.createElement(FolderRibbon, { premiumFolderData: currentGallection.premiumFolderData, className: gallectionSidebarStyles['premium-badge'] }),
        React.createElement("div", { className: gallectionSidebarStyles['sidebar'] },
            renderGallectionCoverBlock(),
            isPaidFolder && renderSubscribersCount(),
            React.createElement("div", { className: gallectionSidebarStyles['form'] },
                renderGallectionNameInput(),
                !currentSubfolder &&
                    canChangeGallectionDescription(currentGallection) &&
                    renderDescriptionTextarea(),
                ((_a = currentGallection.premiumFolderData) === null || _a === void 0 ? void 0 : _a.type) === 'paid' &&
                    renderPriceInput()))));
};
GallectionSidebar.displayName = 'GallectionSidebar';
export default GallectionSidebar;
//# sourceMappingURL=GallectionSidebar.connected.js.map